import { Button, Card, CardContent, Progress } from '@aaas/shared';
import {
  Briefcase,
  Check,
  Database,
  FileUser,
  Megaphone,
  Sparkles,
  UserPlus,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useATSRole } from '../../hooks/useATSRole';
import {
  useDismissDashboard,
  useDismissStep,
  useSeedSampleData,
} from '../../hooks/useOnboardingQueries';
import type {
  OnboardingProgress,
  OnboardingStep,
  OnboardingStepKey,
} from '../../types/onboarding';

interface OnboardingChecklistProps {
  progress: OnboardingProgress;
}

const STEP_ORDER: OnboardingStepKey[] = [
  'create_position',
  'create_campaign',
  'create_job',
  'add_cv',
  'configure_reference',
  'invite_member',
];

const STEP_ICONS: Record<OnboardingStepKey, React.ComponentType<{ className?: string }>> = {
  create_position: Briefcase,
  create_campaign: Megaphone,
  create_job: FileUser,
  add_cv: FileUser,
  configure_reference: Database,
  invite_member: UserPlus,
};

const STEP_ROUTES: Partial<Record<OnboardingStepKey, string>> = {
  create_position: '/ats/positions',
  create_campaign: '/ats/campaigns',
  create_job: '/ats/jobs',
  add_cv: '/ats/cvs',
  invite_member: '/workspace/settings?tab=invitations',
};

export function OnboardingChecklist({ progress }: OnboardingChecklistProps) {
  const { t } = useTranslation('ats');
  const navigate = useNavigate();
  const { isManager, isLoading: roleLoading } = useATSRole();
  // Only managers (or the workspace owner — auto-manager on backend) can
  // trigger the sample-data seed. While the role is still being resolved
  // we treat the user as non-manager to avoid flashing the button.
  const canSeedSampleData = !roleLoading && isManager;

  const dismissDashboard = useDismissDashboard();
  const dismissStep = useDismissStep();
  const seedSampleData = useSeedSampleData();

  const [seedError, setSeedError] = useState<string | null>(null);
  const [seedSuccess, setSeedSuccess] = useState<string | null>(null);

  const completed = progress.completed_count;
  const total = progress.total_count;
  const allDone = completed >= total;
  const percent = Math.round((completed / Math.max(total, 1)) * 100);

  const orderedSteps: OnboardingStep[] = STEP_ORDER.map((key) => {
    const found = progress.steps.find((s) => s.step === key);
    if (found) return found;
    return { step: key, completed: false, dismissed: false, count: 0 };
  });

  const handleStepAction = async (step: OnboardingStep) => {
    if (step.step === 'configure_reference' && !step.completed) {
      setSeedError(null);
      setSeedSuccess(null);
      try {
        const result = await seedSampleData.mutateAsync();
        setSeedSuccess(
          t('onboarding.sampleData.success', {
            positions: result.positions,
            jobs: result.jobs,
            cvs: result.cvs,
          }),
        );
      } catch (err: unknown) {
        const response =
          typeof err === 'object' && err !== null && 'response' in err
            ? (err as {
                response?: {
                  status?: number;
                  data?: { detail?: string; message?: string };
                };
              }).response
            : undefined;
        const status = response?.status;
        const backendMessage =
          response?.data?.detail || response?.data?.message;

        if (status === 409) {
          setSeedError(t('onboarding.sampleData.errorAlreadyHasData'));
        } else if (backendMessage) {
          // Surface the backend's specific reason (e.g. "Workspace has no
          // Pipeline...", "Sample placement is marked rejected but...").
          // Generic toast loses the why for non-409 cases.
          setSeedError(backendMessage);
        } else {
          setSeedError(t('onboarding.sampleData.errorGeneric'));
        }
      }
      return;
    }

    const route = STEP_ROUTES[step.step];
    if (route) {
      navigate(route);
    }
  };

  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
      <CardContent className="p-6">
        {/* Header */}
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                {allDone ? t('onboarding.completedTitle') : t('onboarding.title')}
              </h2>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {allDone
                ? t('onboarding.completedSubtitle')
                : t('onboarding.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => dismissDashboard.mutate()}
            disabled={dismissDashboard.isPending}
            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
            aria-label={t('onboarding.dismissAll')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="mb-1.5 flex items-center justify-between text-xs text-gray-600">
            <span>{t('onboarding.progressLabel', { completed, total })}</span>
            <span className="font-mono">{percent}%</span>
          </div>
          <Progress value={percent} className="h-2" />
        </div>

        {/* Seed banner */}
        {seedSuccess && (
          <div className="mb-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
            {seedSuccess}
          </div>
        )}
        {seedError && (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {seedError}
          </div>
        )}

        {/* Steps */}
        <ul className="space-y-2">
          {orderedSteps.map((step) => {
            const Icon = STEP_ICONS[step.step];
            const isSeedStep = step.step === 'configure_reference';
            const actionLabel = isSeedStep && !step.completed
              ? t('onboarding.step.configure_reference.action')
              : t(`onboarding.step.${step.step}.action`);
            const isPending = isSeedStep && seedSampleData.isPending;
            // Seed action is manager-only; everyone else still sees the step
            // but the trigger button is hidden (a hint takes its place).
            const stepBlockedByRole = isSeedStep && !canSeedSampleData;
            const showAction =
              !step.completed && !step.dismissed && !stepBlockedByRole;
            const showManagerOnlyHint =
              isSeedStep && !step.completed && !step.dismissed && !canSeedSampleData;

            return (
              <li
                key={step.step}
                className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                  step.completed
                    ? 'border-green-200 bg-green-50/50'
                    : step.dismissed
                      ? 'border-gray-200 bg-gray-50 opacity-60'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div
                  className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${
                    step.completed
                      ? 'bg-green-600 text-white'
                      : 'border border-gray-300 bg-white text-gray-400'
                  }`}
                >
                  {step.completed ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <Icon className="h-3.5 w-3.5" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm text-gray-900">
                    {t(`onboarding.step.${step.step}.label`)}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-600">
                    {t(`onboarding.step.${step.step}.description`)}
                  </div>
                  {step.completed && step.count > 0 && (
                    <div className="mt-1 text-xs text-green-700">
                      ✓ {step.count} {step.count === 1 ? 'record' : 'records'}
                    </div>
                  )}
                  {showManagerOnlyHint && (
                    <div className="mt-1 text-xs italic text-gray-500">
                      {t('onboarding.step.configure_reference.managerOnlyHint')}
                    </div>
                  )}
                </div>

                {showAction && (
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <Button
                      size="sm"
                      variant={isSeedStep ? 'default' : 'outline'}
                      disabled={isPending}
                      onClick={() => handleStepAction(step)}
                    >
                      {isPending
                        ? t('onboarding.sampleData.loading')
                        : actionLabel}
                    </Button>
                    <button
                      type="button"
                      onClick={() => dismissStep.mutate(step.step)}
                      disabled={dismissStep.isPending}
                      className="text-xs text-gray-500 hover:text-gray-800 hover:underline disabled:opacity-50"
                    >
                      {t('onboarding.dismiss')}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {allDone && (
          <div className="mt-4 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => dismissDashboard.mutate()}
              disabled={dismissDashboard.isPending}
            >
              {t('onboarding.done')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
